import {
  Injectable,
  PLATFORM_ID,
  PendingTasks,
  inject,
  InjectionToken,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface PageData {
  sections: Array<{
    id: number;
    type: number;
    lang: string;
    order: number;
    title: string;
    description: string;
    active: boolean;
    image: string | null;
  }>;
}

interface SectionsApiResponse {
  type: string;
  msg: string;
  data: PageData['sections'];
}

// Token del lado servidor (inyectado en server.ts a nivel platform).
// SIN providedIn:'root': con binding en root, el root lo resolvería antes que el
// platform y devolvería el default. Sin binding, la inyección sube al platform.
export const GET_PAGE_DATA_FN = new InjectionToken<
  (type: number, lang: string) => Promise<PageData>
>('GET_PAGE_DATA_FN');

@Injectable({ providedIn: 'root' })
export class PageDataService {
  private platformId = inject(PLATFORM_ID);
  private getPageDataFn = inject(GET_PAGE_DATA_FN, { optional: true });
  private http = inject(HttpClient);
  private pendingTasks = inject(PendingTasks);

  async getPageData(type: number, lang: string = 'es'): Promise<PageData> {
    const normalizedLang = lang.split('-')[0];

    if (isPlatformServer(this.platformId)) {
      if (!this.getPageDataFn) return { sections: [] };
      // PendingTasks fuerza al SSR a esperar la query (ver cms-page-data.service).
      const done = this.pendingTasks.add();
      try {
        return await this.getPageDataFn(type, normalizedLang);
      } finally {
        done();
      }
    }

    try {
      const response = await firstValueFrom(
        this.http.get<SectionsApiResponse>(`/api/section/${type}/${normalizedLang}`)
      );
      return { sections: response.data };
    } catch {
      return { sections: [] };
    }
  }
}
